// API configuration
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

// Decode a JWT payload without verifying (client-side expiry check only).
const decodeJwt = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
};

// True once a token is past its `exp`. A 60s skew guards against sending a
// token that expires mid-flight.
export const isTokenExpired = (token) => {
  const payload = decodeJwt(token);
  if (!payload?.exp) return false;
  return payload.exp * 1000 < Date.now() + 60_000;
};

// Utility function to get auth headers.
//
// An expired token is NOT attached: the backend's optionalAuth silently ignores
// it, so the request would go out as anonymous and a write would come back as a
// misleading "You do not have permission" 403. Instead we drop the token and
// fire a one-time `auth:expired` event so the app can prompt a fresh login.
export const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  if (token && isTokenExpired(token)) {
    window.dispatchEvent(new CustomEvent('auth:expired'));
    return { 'Content-Type': 'application/json' };
  }
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// Fetch wrapper that returns { data, status } like axios
// On error (non-2xx), throws an error with a .response property for status checking
export const fetchJson = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers
    }
  });

  let data;
  try {
    data = await response.json();
  } catch {
    data = {};
  }

  if (!response.ok) {
    const error = new Error(data.error || `HTTP ${response.status}: ${response.statusText}`);
    error.response = { status: response.status, data };
    throw error;
  }

  return { data, status: response.status };
};

// Simplified wrapper for internal API calls using relative endpoints
// Returns parsed JSON directly (not wrapped in { data })
export const apiCall = async (endpoint, options = {}) => {
  const response = await fetchJson(`${API_BASE}${endpoint}`, options);
  return response.data;
};

export { API_BASE };
